/* eslint-disable radix */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Section from 'components/shared/Section'
import Heading from 'components/shared/Heading'
import Content from 'components/shared/Content'
import AnimatedNumber from 'react-animated-number'
import VisibilitySensor from 'react-visibility-sensor'

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.dark};
  text-align: center;
  padding: 10px 0 0;
  ${({ theme }) => theme.mq.md} {
    padding: 35px 0;
  }
`

const StyledContent = styled(Content)`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1800px;
`

const Description = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  max-width: 300px;
  text-transform: uppercase;
  font-weight: ${({ theme }) => theme.superBold};
  text-align: center;
  color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => theme.mq.xs} {
    text-align: left;
  }
  ${({ theme }) => theme.mq.md} {
    text-align: center;
  }
`

const FeaturesWrapper = styled.div`
  padding: 55px 0 20px;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 15px;
  width: 100%;
  ${({ theme }) => theme.mq.md} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${({ theme }) => theme.mq.xl} {
    grid-template-columns: repeat(5, 1fr);
  }
`

const Feature = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 15px;
  width: 100%;
  min-height: 250px;
  background: ${({ theme }) => theme.colors.red};
  ${({ theme }) => theme.mq.xs} {
    flex-direction: row;
  }
  ${({ theme }) => theme.mq.md} {
    flex-direction: column;
  }
  ${({ visibleTablet }) =>
    visibleTablet &&
    css`
      display: none;
      ${({ theme }) => theme.mq.md} {
        flex-direction: row !important;
        display: flex;
      }
      ${({ theme }) => theme.mq.xl} {
        display: none;
      }
    `}
  ${({ hiddenTablet }) =>
    hiddenTablet &&
    css`
      display: flex;
      ${({ theme }) => theme.mq.md} {
        display: none;
      }
      ${({ theme }) => theme.mq.xl} {
        display: flex;
      }
    `}
`

const Number = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-weight: ${({ theme }) => theme.superBold};
  text-transform: uppercase;
  font-size: 5.2rem;
  white-space: nowrap;
  padding-bottom: 10px;
  ${({ theme }) => theme.mq.xs} {
    padding-bottom: 0;
    padding-right: 20px;
  }
  ${({ theme }) => theme.mq.md} {
    padding-right: 0;
    padding-bottom: 10px;
  }
`

const StyledDescription = styled(Description)`
  text-align: left !important;
  margin-left: 20px;
`

const prettyNumber = number => {
  return `${number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`
}

const Promo = ({ features }) => {
  const [isInViewport, setIsInViewport] = useState(false)
  const [hasBeenAnimated, setHasBeenAnimated] = useState(false)

  return (
    <Wrapper>
      <Section>
        <StyledContent>
          <Heading big title="Information about" highlight="the group" />
          <FeaturesWrapper>
            {features.map(({ number, unit, text }, index) => (
              <React.Fragment key={index}>
                <VisibilitySensor
                  onChange={visible =>
                    index === 0 && visible && setIsInViewport(true)
                  }
                >
                  <Feature hiddenTablet={index === features.length - 1}>
                    <Number>
                      <AnimatedNumber
                        component="span"
                        value={parseInt(
                          isInViewport || hasBeenAnimated ? number : 0
                        )}
                        duration={1500}
                        frameStyle={perc => {
                          if (perc === number) {
                            setHasBeenAnimated(true)
                          }
                          return {}
                        }}
                        stepPrecision={0}
                        formatValue={n => prettyNumber(n)}
                      />
                      {unit}
                    </Number>
                    <Description dangerouslySetInnerHTML={{ __html: text }} />
                  </Feature>
                </VisibilitySensor>
              </React.Fragment>
            ))}
          </FeaturesWrapper>
          <Feature visibleTablet>
            <Number>
              <AnimatedNumber
                component="span"
                value={parseInt(
                  isInViewport || hasBeenAnimated
                    ? features[features.length - 1].number
                    : 0
                )}
                duration={1500}
                frameStyle={perc => {
                  if (perc === features[features.length - 1].number) {
                    setHasBeenAnimated(true)
                  }
                  return {}
                }}
                stepPrecision={0}
                formatValue={n => prettyNumber(n)}
              />
              {features[features.length - 1].unit}
            </Number>
            <StyledDescription
              dangerouslySetInnerHTML={{
                __html: features[features.length - 1].text,
              }}
            />
          </Feature>
        </StyledContent>
      </Section>
    </Wrapper>
  )
}

Promo.propTypes = {
  features: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default Promo
